import { useEffect, useState } from "react";
import { useLang } from "../hooks/useLang";
import Icon, { type IconName } from "./icons";
import type { Mode } from "./LeftNav";
import { getMe } from "../services/api";
import { listAnalyses } from "../services/api";
import { listDocuments } from "../services/documents";
import { listStaff } from "../services/staff";
import { listProjects } from "../services/projects";
import { listSessions } from "../services/gspr";
import { listChatSessions } from "../services/chat";

type Stat = {
  key: string;
  labelKey: string;
  icon: IconName;
  value: number | null; // null → yükleniyor
  accent: string;
  go?: Mode;
  tab?: string; // Ayarlar sayfasında açılacak tab (settings_tab)
};

// Dashboard — panel anasayfası. Özet istatistik kutuları + hızlı erişim.
export default function Dashboard({ onNavigate }: { onNavigate: (m: Mode) => void }) {
  const { t } = useLang();
  const [orgName, setOrgName] = useState<string>("");
  const [credit, setCredit] = useState<number | null>(null);
  const [docs, setDocs] = useState<number | null>(null);
  const [staff, setStaff] = useState<number | null>(null);
  const [projects, setProjects] = useState<number | null>(null);
  const [sessions, setSessions] = useState<number | null>(null);
  const [analyses, setAnalyses] = useState<number | null>(null);
  const [chat, setChat] = useState<number | null>(null);
  const [docAnalysis, setDocAnalysis] = useState<number | null>(null);

  useEffect(() => {
    // Her kart bağımsız yüklenir; biri hata verse diğerleri gösterilir.
    getMe().then((m) => {
      setCredit(m.credit_balance);
      setOrgName(m.org_name || "");
      setDocAnalysis(m.usage_doc_analysis ?? 0);
    }).catch(() => setCredit(0));
    listDocuments().then((r) => setDocs(r.count)).catch(() => setDocs(0));
    listStaff().then((r) => setStaff(r.length)).catch(() => setStaff(0));
    listProjects().then((r) => setProjects(r.length)).catch(() => setProjects(0));
    listSessions(100).then((r) => setSessions(r.count)).catch(() => setSessions(0));
    listAnalyses(1).then((r) => setAnalyses(r.count)).catch(() => setAnalyses(0));
    listChatSessions().then((r) => setChat(r.length)).catch(() => setChat(0));
  }, []);

  const stats: Stat[] = [
    { key: "credit", labelKey: "dash_credit", icon: "coins", value: credit, accent: "#8b5cf6", go: "settings", tab: "credits" },
    { key: "docs", labelKey: "dash_docs", icon: "file", value: docs, accent: "#16a34a", go: "documents" },
    { key: "staff", labelKey: "dash_staff", icon: "users", value: staff, accent: "#0ea5e9", go: "settings", tab: "staff" },
    { key: "projects", labelKey: "dash_projects", icon: "folder", value: projects, accent: "#f59e0b", go: "settings", tab: "projects" },
    { key: "sessions", labelKey: "dash_sessions", icon: "clipboard", value: sessions, accent: "#ec4899", go: "gspr" },
    { key: "analyses", labelKey: "dash_analyses", icon: "search", value: analyses, accent: "#64748b", go: "analyze" },
    { key: "chat", labelKey: "dash_chat", icon: "chat", value: chat, accent: "#6366f1", go: "chat" },
    { key: "docanalysis", labelKey: "dash_docanalysis", icon: "chartBar", value: docAnalysis, accent: "#14b8a6", go: "docanalysis" },
  ];

  return (
    <div className="dashboard">
      <div className="dash-head">
        <h2>{t("dash_welcome")}{orgName ? `, ${orgName}` : ""}</h2>
        <p className="hint">{t("dash_intro")}</p>
      </div>

      <div className="dash-grid">
        {stats.map((s) => (
          <button
            key={s.key}
            className="stat-card"
            onClick={() => { if (s.tab) localStorage.setItem("settings_tab", s.tab); if (s.go) onNavigate(s.go); }}
            style={{ ["--stat-accent" as string]: s.accent }}
          >
            <span className="stat-ico" style={{ background: s.accent + "1a", color: s.accent }}>
              <Icon name={s.icon} size={22} />
            </span>
            <span className="stat-body">
              <span className="stat-value">{s.value == null ? "…" : s.value}</span>
              <span className="stat-label">{t(s.labelKey)}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="dash-actions">
        <button className="btn-pri" onClick={() => onNavigate("gspr")}>
          <Icon name="clipboard" size={16} /> {t("dash_action_gspr")}
        </button>
        <button className="btn-sec" onClick={() => onNavigate("documents")}>
          <Icon name="file" size={16} /> {t("dash_action_docs")}
        </button>
        <button className="btn-sec" onClick={() => { localStorage.setItem("settings_tab", "credits"); onNavigate("settings"); }}>
          <Icon name="coins" size={16} /> {t("dash_action_credit")}
        </button>
      </div>
    </div>
  );
}
